import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import reportWebVitals from './reportWebVitals'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import theme from './theme'
import './i18n/i18n.config' // Import i18n configuration to initialize translations
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter as Router } from 'react-router-dom'

const PRELOAD_ERROR_RELOAD_ATTEMPTS_KEY = 'malu:preload-error-reload-attempts'
const PRELOAD_ERROR_RELOAD_WINDOW_MS = 60 * 1000
const PRELOAD_ERROR_RELOAD_LIMIT = 2
const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'importing a module script failed',
  'error loading dynamically imported module',
] as const

let preloadErrorReloadAttempts: number[] = []

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message
  }

  return typeof error === 'string' ? error : ''
}

const isDynamicImportError = (error: unknown) => {
  const message = getErrorMessage(error).toLowerCase()
  return DYNAMIC_IMPORT_ERROR_PATTERNS.some((pattern) => message.includes(pattern))
}

const getPreloadErrorReloadAttempts = (now: number) => {
  try {
    preloadErrorReloadAttempts = JSON.parse(sessionStorage.getItem(PRELOAD_ERROR_RELOAD_ATTEMPTS_KEY) || '[]')
  } catch {
    // Some browsers can block sessionStorage; keep the in-memory guard.
  }

  preloadErrorReloadAttempts = preloadErrorReloadAttempts.filter((timestamp) => now - timestamp < PRELOAD_ERROR_RELOAD_WINDOW_MS)
  return preloadErrorReloadAttempts
}

const setPreloadErrorReloadAttempts = (timestamps: number[]) => {
  preloadErrorReloadAttempts = timestamps

  try {
    sessionStorage.setItem(PRELOAD_ERROR_RELOAD_ATTEMPTS_KEY, JSON.stringify(timestamps))
  } catch {
    // Ignore storage failures; the in-memory guard still prevents same-page loops.
  }
}

const reloadAfterDynamicImportError = (event: Event) => {
  const now = Date.now()
  const reloadAttempts = getPreloadErrorReloadAttempts(now)

  if (reloadAttempts.length >= PRELOAD_ERROR_RELOAD_LIMIT) {
    return
  }

  event.preventDefault()
  setPreloadErrorReloadAttempts([...reloadAttempts, now])
  window.location.reload()
}

const registerPreloadErrorRecovery = () => {
  window.addEventListener('vite:preloadError', (event) => {
    reloadAfterDynamicImportError(event)
  })

  window.addEventListener('unhandledrejection', (event) => {
    if (isDynamicImportError(event.reason)) {
      reloadAfterDynamicImportError(event)
    }
  })
}

registerPreloadErrorRecovery()

const queryClient = new QueryClient()
const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)
root.render(
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <QueryClientProvider client={queryClient}>
      <Router
        future={{
          v7_relativeSplatPath: true,
          v7_startTransition: true,
        }}
      >
        <App />
      </Router>
    </QueryClientProvider>
  </ThemeProvider>
)

reportWebVitals()
